/**
 * The "I forgot my password" request form.
 *
 * In plain words: it asks for an email address and sends a reset link to it. Whatever
 * address is typed in, the answer is the same sentence, because a different answer for an
 * address that has an account would let anyone find out who banks here (FR-003a, SC-003).
 *
 * There are exactly TWO answers, not three:
 *
 *  1. The request reached the server, whatever the server then said — including a 429
 *     "you already asked a minute ago" and a real 4xx. One identical message.
 *  2. The request never reached the server (no status at all, status 0, or a 5xx). A
 *     different message that says so and invites a retry. Hiding this one would leave
 *     someone waiting for an email nobody sent (FR-003b).
 *
 * The rate limit deliberately has no message of its own. Supabase counts recovery requests
 * per user row, so only an address that already has an account can be rate-limited; a
 * separate "wait a minute" reply would be a free account-existence check (founder's
 * decision, 2026-09-26). The single wording carries the advice anyway.
 *
 * Requirements: FR-001 (the form half), FR-002, FR-003, FR-003(a)(b), SC-003.
 */

import { useState } from "react";
import { authClient } from "@/services/auth";
import { RESET_PATH } from "@/services/auth/recovery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { AuthError } from "@/services/auth";

/** The one answer for every address whose request reached the server. */
const SENT_NOTICE =
  "If there's an account for that address, a link is on its way. " +
  "If you asked for one in the last minute, that link is the one to use.";

/** The only thing this form admits: the request never left the building. */
const NOT_SENT_NOTICE =
  "That request didn't go through, so no link has been sent. " +
  "Check your connection and try again.";

/**
 * Did the request fail to reach the server? No status means the failure happened before
 * any answer came back (a dead connection, a client-side throw); status 0 is the same
 * thing seen through fetch; 5xx means the server took it and fell over. Everything else
 * — 400, 404, 422, 429 — is the server answering, and gets the identical message.
 */
const neverReachedServer = (error: AuthError): boolean => {
  const status = error.status;
  if (status === undefined || status === null) return true;
  return status === 0 || status >= 500;
};

interface ForgotPasswordFormProps {
  /** Back to the login fields, without a page reload (FR-001). */
  onBack: () => void;
}

export const ForgotPasswordForm = ({ onBack }: ForgotPasswordFormProps) => {
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // A second click while the first request is in flight must not send a second email
    // (spec: double submission). The button is disabled too; this is the belt to its braces.
    if (pending) return;
    if (!email.trim()) return;

    setPending(true);
    setNotice(null);
    try {
      const { error } = await authClient.resetPassword(email, {
        // Say explicitly where the emailed link should come back to, so a reset started
        // on localhost or a preview build does not land on the live site (FR-002, SC-004).
        redirectTo: `${window.location.origin}${RESET_PATH}`,
      });
      setNotice(error && neverReachedServer(error) ? NOT_SENT_NOTICE : SENT_NOTICE);
    } catch {
      // A thrown call never reached the server either. Same answer as the branch above,
      // and never the one that claims a link is on its way.
      setNotice(NOT_SENT_NOTICE);
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="reset-email">Email</Label>
        <Input
          id="reset-email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <p className="text-sm text-muted-foreground">
          We'll send a link that lets you choose a new password.
        </p>
      </div>

      <Button type="submit" className="w-full" disabled={pending}>
        {pending ? "Sending..." : "Send reset link"}
      </Button>

      {notice && (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      )}

      <div className="text-center">
        <Button
          type="button"
          variant="link"
          onClick={onBack}
          className="text-sm text-muted-foreground"
        >
          Back to log in
        </Button>
      </div>
    </form>
  );
};
